import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Filter from 'components/Filter';
import SearchField from 'components/SearchField';
import { SEPARATOR } from 'components/Form/Input/SearchSelect';
import { Input, Label } from 'reactstrap';
import usePrevious from 'hooks/usePrevious';
import { useSelector } from "react-redux";
import classNames from 'classnames';
import { compareArrayValues } from 'helpers/util';

const InputOptions = ({
    options,
    single,
    selectedOptionMap,
    setSelectedOptionMap,
    keyword,
    title,
    isForcedOpen,
    disabled,
}) => {
    const optionStyle = classNames({
        'filter-option': true,
        'disabled': disabled,
    });
    const handleChange = ({ text, value }) => e => {
        const valueString = Array.isArray(value) ? [...value].sort(compareArrayValues).toString() : value;

        if (e.currentTarget.checked) {
            if (single) {
                setSelectedOptionMap([]);
            }
            setSelectedOptionMap({
                ...selectedOptionMap,
                [valueString]: { text, value },
            });
        }
        else {
            setSelectedOptionMap({
                ...selectedOptionMap,
                [valueString]: undefined,
            });
        }

    };

    return options.map(({ text, value }) => {
        const valueString = Array.isArray(value) ? [...value].sort(compareArrayValues).toString() : [value].toString();
        if (text === SEPARATOR) {
            return null;
        }

        return (
            text.toUpperCase().indexOf(keyword.toUpperCase()) > -1 ?
                (
                    <Label className={ optionStyle } key={ value }>
                        <Input
                            type={ single ? 'radio' : 'checkbox' }
                            name={ single ? title : undefined }
                            checked={
                                selectedOptionMap[valueString] !== undefined
                            }
                            onChange={ handleChange({ text, value }) }
                            disabled={ disabled }
                            data-checked={ selectedOptionMap[valueString] !== undefined }
                        />
                        <span title={ isForcedOpen ? text : undefined }>{ text }</span>
                    </Label>
                ) :
                null
        );
    });
};

InputOptions.propTypes = {
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.oneOfType([
            PropTypes.node,
            PropTypes.number,
            PropTypes.string,
            PropTypes.bool,
        ]),
        text: PropTypes.string,
    })).isRequired,
    single: PropTypes.bool,
    selectedOptionMap: PropTypes.oneOfType([
        PropTypes.shape({}),
        PropTypes.arrayOf(PropTypes.shape()),
    ]),
    keyword: PropTypes.string,
    title: PropTypes.string,
    setSelectedOptionMap: PropTypes.func,
    isForcedOpen: PropTypes.bool,
    disabled: PropTypes.bool,
};

InputOptions.defaultProps = {
    single: false,
    keyword: '',
    title: '',
    setSelectedOptionMap: () => {},
    isForcedOpen: false,
    disabled: false,
};


const MultiselectFilter = ({
    defaultSelected,
    title,
    options,
    onChange,
    searchable,
    single,
    isForcedOpen,
    disabled,
}) => {
    const maxCount = 3;
    const locale = useSelector(state => state.i18n.locale);
    const [selectedOptionMap, setSelectedOptionMap] = useState({});
    const [open, setOpen] = useState(false);
    const [keyword, setKeyword] = useState('');
    const [inputSelect, setInputSelect] = useState({});
    const [applied, setApplied] = useState(inputSelect.length>0);
    const [filterValue, setFilterValue] = useState('');
    const prevApplied = usePrevious(applied);
    const prevInputSelect = usePrevious(inputSelect);

    const handleFilterReset = () => {
        setOpen(false);
        setKeyword('');
        setSelectedOptionMap({});
        setApplied(false);
        setInputSelect({});
        onChange({ inputSelect: [] });
    };

    const handleOptionChange = obj => {
        setSelectedOptionMap(obj);
        if (isForcedOpen) {
            const inputSelect = Object.values(obj).filter(i => i !== undefined);
            onChange({
                inputSelect: inputSelect.map((item) => (item?.value !== undefined) ? item.value : ''),
            });
        }
    };

    const handleFilterApply = () => {
        const inputSelect = Object.values(selectedOptionMap).filter(i => i !== undefined);
        const result = inputSelect.map((item) => {

            if (Array.isArray(item.value)) {
                return ([...item.value].sort(compareArrayValues).toString() !== undefined) ? item.value : '';
            }
            else {
                return (item?.value !== undefined) ? item.value : '';
            }

        });
        setOpen(false);
        setKeyword('');
        setInputSelect(inputSelect);
        setApplied(inputSelect.length>0);
        onChange({
            inputSelect: result,
        });

    };
    const handleFilterToggle = () => {
        if (!disabled) {
            setKeyword('');
            setOpen(!open);
        }

    }
    const handleKeywordChange = keyword => {
        setKeyword(keyword);
    }

    const renderSearchField = () => {
        let el;

        if (searchable) {
            el = (
                <SearchField
                    stopTriggerByEnter
                    tag="div"
                    onChange={ handleKeywordChange }
                    value={ keyword }
                    disabled={ disabled }
                />
            );
        }

        return el;
    };

    useEffect(() => {
        if (!defaultSelected.length) {
            setOpen(false);
            setKeyword('');
            setSelectedOptionMap({});
            setApplied(false);
            setInputSelect({});
        }
        else {
            const mapObject = options.reduce((obj, item) => {
                const { value } = item;
                if (Array.isArray(value)) {
                    defaultSelected.forEach(element => {
                        const current = [...element].sort(compareArrayValues).toString();
                        const target = [...value].sort(compareArrayValues).toString();
                        if (current === target) {
                            obj[target] = item;
                        }
                    });
                }
                else if (defaultSelected.indexOf(value) > -1) {
                    obj[value] = item;
                }

                return obj;
            }, {});
            const inputSelect = Object.values(mapObject).filter(i => i !== undefined);
            setSelectedOptionMap(mapObject);
            setInputSelect(inputSelect);
            if (!isForcedOpen) {
                setApplied(true);
            }
        }
    // eslint-disable-next-line
    }, [defaultSelected, locale, options]);

    useEffect(() => {
        let filterValueArry = [];
        if (!isForcedOpen) {
            if (!applied || prevApplied !== applied || prevInputSelect !== inputSelect) {
                for (let i = 0; i < Math.min(maxCount, inputSelect.length); i++) {
                    if (inputSelect[i].value !== undefined) {
                        filterValueArry.push(inputSelect[i].text);
                    }

                }
                if (inputSelect.length > maxCount) {
                    filterValueArry.push('...');
                }

                setFilterValue(filterValueArry.length ? `${ title }: ${ filterValueArry.toString() }` : title);
            }
        }
        else {
            setFilterValue(title);
        }
    }, [applied, inputSelect, prevApplied, title, prevInputSelect, isForcedOpen]);

    return (
        <Filter
            className="multiselect-filter"
            value={ filterValue }
            open={ open }
            applied={ applied }
            onToggle={ handleFilterToggle }
            onReset={ handleFilterReset }
            onApply={ handleFilterApply }
            isForcedOpen={ isForcedOpen }
        >
            { renderSearchField() }
            <div className="option-container">
                <InputOptions
                    selectedOptionMap={ selectedOptionMap }
                    setSelectedOptionMap={ handleOptionChange }
                    keyword={ keyword }
                    title={ title }
                    single={ single }
                    options={ options }
                    isForcedOpen={ isForcedOpen }
                    disabled={ disabled }
                />
            </div>
        </Filter>
    );
};
MultiselectFilter.propTypes = {
    defaultSelected: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.array,
        PropTypes.string,
        PropTypes.bool,
        PropTypes.arrayOf(PropTypes.string),
    ])),
    title: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.oneOfType([
            PropTypes.node,
            PropTypes.bool,
        ]),
        text: PropTypes.string,
    })).isRequired,
    onChange: PropTypes.func,
    searchable: PropTypes.bool,
    single: PropTypes.bool,
    isForcedOpen: PropTypes.bool,
    disabled: PropTypes.bool,
};

MultiselectFilter.defaultProps = {
    defaultSelected: [],
    title: '',
    onChange: () => {},
    searchable: false,
    single: false,
    isForcedOpen: false,
    disabled: false,
};


export default MultiselectFilter;
